import { useCallback, useEffect, useRef } from 'react';
import ResizeObserverPolyfill from 'resize-observer-polyfill';

import { NAV_MENU_ITEM_FLEX_GAP } from '../Components/styleConstants';
import { useResponsiveController } from '../ContextState/useResponsiveController';
import { DATA_TEST } from '../enums';
import { useEnhancedEffect } from '../utils/utils';

import {
  IMenuItemDimensions,
  evaluateHiddenItemRestoration,
  getAvailableContainerSpace,
  getNavContainerWidths,
  handleNavOverflowing
} from './utils';

interface IUseNavResizeControllerProps {
  menuItemsContainerRef: React.RefObject<HTMLDivElement>;
  dynamicMenuItemCount: number;
  isMoreMenuVisible: boolean;
  totalMenuItemCount: number;
  updateDynamicMenuItemCount: (changeDelta: number) => void;
}

/**
 * Hook to manage responsive behavior of navigation items based on container width.
 * Handles showing/hiding menu items and utility tray as window resizes.
 */
export const useNavResizeController = ({
  menuItemsContainerRef,
  dynamicMenuItemCount,
  isMoreMenuVisible,
  totalMenuItemCount,
  updateDynamicMenuItemCount
}: IUseNavResizeControllerProps) => {
  // Polyfill ResizeObserver for older browsers
  useEnhancedEffect(() => {
    if (!window.ResizeObserver) {
      window.ResizeObserver = ResizeObserverPolyfill;
    }
  }, []);

  const {
    navBarContainerRef,
    setCondensedUtilityTray,
    condensedUtilityTray,
    mobileBreakpointOverride,
    setMobileBreakpointOverride
  } = useResponsiveController();

  const animationFrameIdRef = useRef<number>(null);
  const isMountedRef = useRef(true);

  // Cache last measured outer width to skip no-op resize events (e.g. height-only changes)
  const lastContainerWidthRef = useRef<number>(-1);

  // Cache utility container width to determine if it can be shown again after being hidden
  const utilContainerWidthRef = useRef<number>(200);

  // Track dimensions of menu items that get hidden due to container width constraints
  const hiddenElementDimensions = useRef<IMenuItemDimensions[]>([]);

  // --- Synced refs for state values read inside observer callbacks ---
  const dynamicMenuItemCountRef = useRef(dynamicMenuItemCount);
  dynamicMenuItemCountRef.current = dynamicMenuItemCount;

  const condensedUtilityTrayRef = useRef(condensedUtilityTray);
  condensedUtilityTrayRef.current = condensedUtilityTray;

  const isMoreMenuVisibleRef = useRef(isMoreMenuVisible);
  isMoreMenuVisibleRef.current = isMoreMenuVisible;

  const totalMenuItemCountRef = useRef(totalMenuItemCount);
  totalMenuItemCountRef.current = totalMenuItemCount;

  useEffect(() => {
    // Reset hidden elements when menu item count changes to keep the hidden elements in sync
    hiddenElementDimensions.current = [];
  }, [totalMenuItemCount]);

  // Core resize handling logic - determines if items need to be hidden or can be restored
  // Reads all state from refs so the callback identity is stable (no deps that change).
  const handleNavResizeUpdates = useCallback(
    (navContainerEl: HTMLDivElement, forceCheck = false) => {
      if (!navContainerEl) return;

      // Skip measurement entirely when container has zero width (hidden/display:none)
      const currentWidth = navContainerEl.clientWidth;
      if (currentWidth === 0) return;

      // Skip if width hasn't changed since last check (e.g. height-only resize)
      // unless forceCheck is set (state change, visibility change, etc.)
      if (!forceCheck && currentWidth === lastContainerWidthRef.current) return;
      lastContainerWidthRef.current = currentWidth;

      const currentIsMoreMenuVisible = isMoreMenuVisibleRef.current;
      const currentCondensedUtilityTray = condensedUtilityTrayRef.current;
      const currentDynamicMenuItemCount = dynamicMenuItemCountRef.current;
      const currentTotalMenuItemCount = totalMenuItemCountRef.current;
      const isUtilityTrayVisible = !currentCondensedUtilityTray;

      const availableMenuContainerSpace = getAvailableContainerSpace(menuItemsContainerRef.current);
      const { navOverflowWidth } = getNavContainerWidths(navContainerEl) || {};

      const areMenuItemsInSync =
        currentTotalMenuItemCount - currentDynamicMenuItemCount === hiddenElementDimensions.current.length;
      if (!areMenuItemsInSync) {
        return;
      }
      // If nav is overflowing or no space available, hide items until it fits
      if (navOverflowWidth > 0 || availableMenuContainerSpace <= 0.01) {
        handleNavOverflowing({
          menuItemsContainer: menuItemsContainerRef.current,
          isUtilityTrayVisible,
          hideUtilityTray: () => {
            setCondensedUtilityTray(true);
          },
          triggerMobileBreakpoint: () => {
            setMobileBreakpointOverride(navContainerEl.scrollWidth);
          },
          hideNextItem: (nextHiddenItemDims) => {
            hiddenElementDimensions.current = [nextHiddenItemDims, ...hiddenElementDimensions.current];
            updateDynamicMenuItemCount(-1);
          }
        });
        return;
      }

      // If space is available but nothing is hidden and tray is visible, nothing to restore
      if (hiddenElementDimensions.current.length === 0 && isUtilityTrayVisible) {
        return;
      }

      // If space becomes available, evaluate whether hidden items or utility tray can be restored
      if (availableMenuContainerSpace > 0.01) {
        evaluateHiddenItemRestoration({
          menuItemsContainer: menuItemsContainerRef.current,
          hiddenItemDims: hiddenElementDimensions.current,
          hiddenUtilContainerWidth: utilContainerWidthRef.current,
          isMoreMenuVisible: currentIsMoreMenuVisible,
          availableSpace: availableMenuContainerSpace,
          isUtilityTrayVisible,
          showUtilityTray: () => {
            setCondensedUtilityTray(false);
          },
          showNextHiddenItem: () => {
            hiddenElementDimensions.current = hiddenElementDimensions.current.slice(1);
            updateDynamicMenuItemCount(1);
          },
          itemFlexGap: NAV_MENU_ITEM_FLEX_GAP
        });
      }
    },
    // Stable deps — setters and refs don't change identity
    [menuItemsContainerRef, setCondensedUtilityTray, setMobileBreakpointOverride, updateDynamicMenuItemCount]
  );

  // Shared helper: schedule a resize check via rAF, deduplicating with the single ID ref
  const runResizeCheck = useCallback(
    (forceCheck = false) => {
      if (!isMountedRef.current) return;
      if (animationFrameIdRef.current != null) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      animationFrameIdRef.current = requestAnimationFrame(() => {
        animationFrameIdRef.current = null;
        if (!isMountedRef.current) return;
        handleNavResizeUpdates(navBarContainerRef.current, forceCheck);
      });
    },
    [handleNavResizeUpdates, navBarContainerRef]
  );

  // Initialize utility container width reference on mount
  useEnhancedEffect(() => {
    const initialUtilContainerWidth = getNavContainerWidths(navBarContainerRef?.current)?.utilContainerWidth;
    if (initialUtilContainerWidth) {
      utilContainerWidthRef.current = initialUtilContainerWidth;
    }
  }, []);

  // --- Tier 1: Stable MutationObserver — created once on mount ---
  useEnhancedEffect(() => {
    // Defer all measurement to the rAF — avoid synchronous layout reflow in mutation callback
    const utilityContainerMutationObserver = new MutationObserver(() => {
      runResizeCheck(true);
    });

    const utilityContainerEl = navBarContainerRef.current?.querySelector(
      `[data-test="${DATA_TEST.nav_utilities_container}"]`
    );

    if (utilityContainerEl) {
      utilityContainerMutationObserver.observe(utilityContainerEl, {
        childList: true,
        attributes: true,
        characterData: true,
        subtree: true
      });
    }
    return () => {
      utilityContainerMutationObserver.disconnect();
    };
  }, [navBarContainerRef, runResizeCheck]);

  // --- Tier 1: Stable ResizeObserver on outer container — created once on mount ---
  useEnhancedEffect(() => {
    const navResizeObserver = new ResizeObserver(() => {
      runResizeCheck();
    });

    if (navBarContainerRef.current) {
      navResizeObserver.observe(navBarContainerRef.current);
    }

    // Initial resize check on mount
    runResizeCheck(true);

    return () => {
      navResizeObserver.disconnect();
    };
  }, [navBarContainerRef, runResizeCheck]);

  // --- Tier 3: ResizeObserver on inner menu-items container ---
  useEnhancedEffect(() => {
    const innerResizeObserver = new ResizeObserver(() => {
      runResizeCheck(true);
    });

    if (menuItemsContainerRef.current) {
      innerResizeObserver.observe(menuItemsContainerRef.current);
    }

    return () => {
      innerResizeObserver.disconnect();
    };
  }, [menuItemsContainerRef, runResizeCheck]);

  // --- Tier 2: IntersectionObserver for visibility detection ---
  useEnhancedEffect(() => {
    if (!('IntersectionObserver' in window)) return;

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          runResizeCheck(true);
        }
      },
      { threshold: [0] }
    );

    if (navBarContainerRef.current) {
      visibilityObserver.observe(navBarContainerRef.current);
    }

    return () => {
      visibilityObserver.disconnect();
    };
  }, [navBarContainerRef, runResizeCheck]);

  // --- Tier 1: Recalculate after state changes (replaces effect-dep-driven observer recreation) ---
  useEffect(() => {
    runResizeCheck(true);
  }, [
    dynamicMenuItemCount,
    condensedUtilityTray,
    mobileBreakpointOverride,
    isMoreMenuVisible,
    totalMenuItemCount,
    runResizeCheck
  ]);

  // Cleanup mounted guard
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (animationFrameIdRef.current != null) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, []);
};
